/**
 * ChoreCard - one chore as it appears on the marketplace and dashboards
 * ---------------------------------------------------------------------
 * Shows the title, pay, where and when, and a badge or two. The whole card
 * links through to the full chore page.
 */

import { Link } from 'react-router-dom';
import { MapPin, Calendar, Clock, Users, AlertTriangle, UserPlus } from 'lucide-react';

import { useI18n } from '../hooks/useI18n';
import Badge, { StatusBadge } from './Badge';
import StarRating from './StarRating';
import { money, dateTime, travel, countdown } from '../utils/format';
import { choreWhenShort } from '../utils/schedule';

export default function ChoreCard({
  chore,
  showStatus = false,
  showApplicants = false,
  // On the marketplace, a chore with somebody booked but no standby is an
  // invitation: "apply as backup". On the poster's own dashboard the same
  // state is a warning to them: "no backup". Same field, opposite audiences,
  // so the caller says which one it is rather than the card guessing.
  invitesBackup = false,
}) {
  // On some screens (your own dashboard) postedBy is just an id rather than
  // the full person, so only treat it as a poster when the details are there.
  const poster = chore.postedBy?.firstName ? chore.postedBy : null;
  const { t } = useI18n();

  return (
    <article className="card card-hover">
      <div className="card-header">
        <Link to={`/chores/${chore._id}`} className="card-title">
          {chore.title}
        </Link>
        {/* On a multi-day job the headline figure is what the whole booking
            pays, with the per-visit rate underneath. A student scanning the
            marketplace is comparing jobs, and "€10" next to a week of dog
            walking would have them comparing the wrong number. */}
        <span className="pay-amount">
          {money(chore.isMultiDay ? chore.totalPay : chore.pay)}
          {chore.isMultiDay && (
            <span className="pay-each">{money(chore.pay)} × {chore.visitCount}</span>
          )}
        </span>
      </div>

      <div className="row" style={{ gap: 'var(--space-xs)', marginBottom: 'var(--space-xs)' }}>
        {/* The stored value is the English enum; only the LABEL is translated.
            Translating the value itself would make the chore invisible to
            every filter - see the warning at the top of i18n/en.js. */}
        <Badge tone="soft-blue">{t(`categories.${chore.category}`)}</Badge>

        {showStatus && <StatusBadge status={chore.status} />}

        {chore.distanceKm !== null && chore.distanceKm !== undefined && (
          <Badge tone="soft-amber" icon={MapPin}>
            {travel(chore)}
          </Badge>
        )}

        {chore.needsBackup && (
          <Badge tone="amber" icon={invitesBackup ? UserPlus : AlertTriangle}>
            {invitesBackup ? t('chore.applyAsBackup') : t('chore.noBackup')}
          </Badge>
        )}
      </div>

      <p className="card-body-text">{chore.description}</p>

      <div className="card-meta">
        <span>
          <MapPin size={14} aria-hidden="true" />
          {chore.location?.city}
        </span>
        <span>
          <Calendar size={14} aria-hidden="true" />
          {chore.isMultiDay
            ? t('chore.daysFrom', { count: chore.visitCount, date: choreWhenShort(chore) })
            : choreWhenShort(chore)}
        </span>
        {chore.estimatedHours ? (
          <span>
            <Clock size={14} aria-hidden="true" />
            {chore.isMultiDay
              ? t('chore.aboutHoursEach', { hours: chore.estimatedHours })
              : t('chore.aboutHours', { hours: chore.estimatedHours })}
          </span>
        ) : null}
        {showApplicants && (
          <span>
            <Users size={14} aria-hidden="true" />
            {chore.applicationCount || 0} applicant
            {chore.applicationCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className="card-footer">
        {poster ? (
          <span className="text-sm text-muted">
            {poster.firstName} {poster.lastName?.charAt(0)}.
            {poster.rating?.count > 0 && (
              <>
                {' '}
                <StarRating value={poster.rating.average} size={13} />
              </>
            )}
          </span>
        ) : (
          <span className="text-sm text-muted">{countdown(chore.hoursUntilStart)}</span>
        )}

        <Link to={`/chores/${chore._id}`} className="btn btn-primary btn-sm btn-inline">
          {t('misc.viewDetails')}
        </Link>
      </div>
    </article>
  );
}
